import { Check, ShieldCheck, TestTube2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { PageHeroCard } from "@/components/site/PageHeroCard";
import { useLang } from "@/i18n/LangContext";
import { companyPages } from "@/i18n/companyPages";
import {
  companyInquiryHref,
  companyPaths,
  drugSources,
  type CompanyLang,
} from "@/data/company";

export const useCompanyLang = () => {
  const { lang, t } = useLang();
  return { lang: lang as CompanyLang, t, copy: companyPages[lang] };
};

export const CompanyPageShell = ({
  breadcrumbLabel,
  children,
}: {
  breadcrumbLabel: string;
  children: React.ReactNode;
}) => {
  const { lang, t } = useCompanyLang();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {children}
        <Breadcrumbs
          items={[
            { label: lang === "sv" ? "Hem" : "Home", to: lang === "sv" ? "/" : "/en" },
            { label: t.nav.company, to: companyPaths.hub[lang] },
            { label: breadcrumbLabel },
          ]}
        />
      </main>
      <Footer />
    </div>
  );
};

export const CompanyPageHero = ({
  label,
  title,
  children,
}: {
  label: string;
  title: string;
  children: React.ReactNode;
}) => {
  const { copy } = useCompanyLang();
  return (
    <PageHeroCard compact>
      <div className="animate-fade-up max-w-2xl">
        <span className="inline-flex h-7 items-center justify-center rounded-full bg-white/20 px-3.5 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
          {copy.shared.forCompanies} · {label}
        </span>
        <h1 className="mt-4 text-balance font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl">
          {title}
        </h1>
        <div className="mt-5 space-y-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
          {children}
        </div>
      </div>
    </PageHeroCard>
  );
};

export const CompanyContactCTA = ({
  title,
  service,
}: {
  title?: string;
  service?: string;
} = {}) => {
  const { lang, copy } = useCompanyLang();
  return (
    <section className="px-2 py-16 sm:px-3 lg:px-4 lg:py-20">
      <div className="relative mx-auto w-full max-w-[96rem] overflow-hidden rounded-2xl bg-primary px-8 py-14 text-primary-foreground shadow-elevated sm:rounded-3xl lg:flex lg:items-center lg:justify-between lg:gap-10 lg:px-16 lg:py-20">
        <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="relative max-w-2xl">
          <span className="text-sm font-semibold uppercase tracking-wider text-primary-foreground/80">
            {copy.shared.contactEyebrow}
          </span>
          <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
            {title ?? copy.shared.contactDefaultTitle}
          </h2>
          <p className="mt-3 text-base text-primary-foreground/85">{copy.shared.contactText}</p>
        </div>
        <div className="relative mt-6 shrink-0 lg:mt-0">
          <Button asChild variant="ghostOnPrimary" size="xl">
            <a href={companyInquiryHref(lang, service)}>
              {copy.shared.inquiryCta}
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export const DrugAssurance = () => {
  const { copy } = useCompanyLang();
  const items = [
    { icon: ShieldCheck, title: copy.shared.assuranceMroTitle, text: copy.shared.assuranceMroText },
    { icon: TestTube2, title: copy.shared.assuranceLabTitle, text: copy.shared.assuranceLabText },
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {items.map(({ icon: Icon, title, text }) => (
        <div
          key={title}
          className="flex items-start gap-4 rounded-2xl border border-border/60 bg-card/80 sm:rounded-3xl px-6 py-5 shadow-card"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </div>
          <p className="text-sm leading-relaxed">
            <strong className="block font-semibold text-foreground">{title}</strong>
            <span className="text-muted-foreground">{text}</span>
          </p>
        </div>
      ))}
    </div>
  );
};

export const CompanyChecklist = ({ items }: { items: string[] }) => (
  <ul className="space-y-3">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Check className="h-3.5 w-3.5" />
        </span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export const DrugSources = () => {
  const { lang, copy } = useCompanyLang();
  return (
    <div className="container-wide pb-10">
      <p className="text-sm leading-relaxed text-muted-foreground">
        {copy.drogtestning.sourcesPrefix}{" "}
        <a href={drugSources.unilabs} className="text-primary underline-offset-2 hover:underline" target="_blank" rel="noreferrer">
          {copy.drogtestning.sourcesUnilabs}
        </a>
        ,{" "}
        <a href={drugSources.instructions} className="text-primary underline-offset-2 hover:underline" target="_blank" rel="noreferrer">
          {copy.drogtestning.sourcesInstructions}
        </a>
        ,{" "}
        <a href={drugSources.mro} className="text-primary underline-offset-2 hover:underline" target="_blank" rel="noreferrer">
          {copy.drogtestning.sourcesMro}
        </a>{" "}
        {lang === "sv" ? "och" : "and"}{" "}
        <a href={drugSources.faq} className="text-primary underline-offset-2 hover:underline" target="_blank" rel="noreferrer">
          {copy.drogtestning.sourcesFaq}
        </a>
        .{" "}
        <a href={drugSources.privacy} className="text-primary underline-offset-2 hover:underline" target="_blank" rel="noreferrer">
          {copy.drogtestning.sourcesPrivacy}
        </a>
        . {copy.shared.sourcesChecked}
      </p>
    </div>
  );
};

export const CompanyServiceRow = ({
  title,
  intro,
  price,
  actions,
}: {
  title: string;
  intro: string;
  price?: string;
  actions: React.ReactNode;
}) => (
  <article className="flex flex-col gap-5 rounded-2xl border border-border/60 bg-card p-6 sm:rounded-3xl shadow-card sm:flex-row sm:items-start sm:justify-between sm:gap-8">
    <div className="min-w-0 flex-1">
      <h3 className="font-display text-xl font-semibold text-foreground">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{intro}</p>
      {price ? (
        <p className="mt-3 text-sm font-semibold text-foreground">{price}</p>
      ) : null}
    </div>
    <div className="flex shrink-0 flex-col gap-2 sm:items-end">{actions}</div>
  </article>
);

